// Letting someone know a person joined while they waited in another tab:
// a browser notification (only if they said yes) and a tab title that stands out.

export const canNotify = () => typeof window !== 'undefined' && 'Notification' in window;

export const notifyPermission = (): NotificationPermission | 'unsupported' =>
  canNotify() ? Notification.permission : 'unsupported';

// Ask only when the person taps "Notify me", never on its own.
export const askToNotify = async (): Promise<NotificationPermission | 'unsupported'> => {
  if (!canNotify()) return 'unsupported';
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
};

// While the tab is in the background, its title says what happened; it goes back
// to normal as soon as the person looks at the tab again.
let savedTitle: string | null = null;
let unread = 0;
const restoreTitle = () => {
  if (document.visibilityState !== 'visible') return;
  if (savedTitle !== null) document.title = savedTitle;
  savedTitle = null;
  unread = 0;
  document.removeEventListener('visibilitychange', restoreTitle);
};
const setBackgroundTitle = (title: string) => {
  if (savedTitle === null) savedTitle = document.title;
  document.title = title;
  document.addEventListener('visibilitychange', restoreTitle);
};

// A new message while the tab is in the background: "New message", then a count.
export const announceMessage = () => {
  if (document.visibilityState === 'visible') return;
  unread += 1;
  setBackgroundTitle(unread === 1 ? '💬 New message · Zuzu Club' : `💬 ${unread} new messages · Zuzu Club`);
};

// Called when a chat starts. Does nothing if the person is already looking.
export const announceMatch = (body: string) => {
  if (document.visibilityState === 'visible') return;

  setBackgroundTitle('💬 Chat connected · Zuzu Club');

  if (notifyPermission() !== 'granted') return;
  const title = 'Someone is here to talk 🤍';
  const options: NotificationOptions = { body, icon: '/icons/icon-192.png', badge: '/icons/icon-192.png', tag: 'zuzu-match' };
  // Phones (Android Chrome) only allow notifications through the service worker.
  const viaWorker = () => navigator.serviceWorker?.ready.then(reg => reg.showNotification(title, options)).catch(() => {});
  try {
    const n = new Notification(title, options);
    n.onclick = () => { window.focus(); n.close(); };
  } catch {
    viaWorker();
  }
};
